
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { ExerciseImage } from "./ExerciseImage"
import { useTranslations } from "@/hooks/useTranslations"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"

interface ExerciseDetailsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  exercise: {
    name: string
    body_part?: string
    equipment?: string
    target_muscle?: string
    image_url?: string
    gif_url?: string
    instructions?: string[]
    isCustom?: boolean
  } | null
}

export function ExerciseDetailsDialog({
  open,
  onOpenChange,
  exercise
}: ExerciseDetailsDialogProps) {
  const { t } = useTranslations("dashboard-exercise-library");

  if (!exercise) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto select-none mt-16 md:mt-0" 
        onContextMenu={(e) => e.preventDefault()}
      >
        <DialogHeader className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 rounded-full hover:bg-slate-100 dark:hover:bg-gray-700"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
          <DialogTitle className="text-xl font-bold flex items-center gap-2 pr-8">
            {exercise.name}
            {exercise.isCustom && (
              <span className="px-2 py-0.5 text-xs bg-flame/10 border border-flame/30 rounded text-flame">
                {t("custom.badge")}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("details")}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid md:grid-cols-2 gap-6 mt-4">
          <div>
            <ExerciseImage
              imagePath={exercise.image_url || exercise.gif_url || ""}
              altText={exercise.name}
              fallbackText={exercise.name}
              className="w-full aspect-square rounded-xl overflow-hidden bg-slate-50 dark:bg-gray-800"
              isCustomExercise={!!exercise.isCustom}
            />
          </div>
          
          <div className="space-y-6">
            <div className="space-y-4">
              {exercise.body_part && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground dark:text-gray-400">{t("bodyPart")}</h3>
                  <p className="text-base dark:text-white">{exercise.body_part}</p>
                </div>
              )}
              
              {exercise.equipment && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground dark:text-gray-400">{t("equipment")}</h3>
                  <p className="text-base dark:text-white">{exercise.equipment}</p>
                </div>
              )}
              
              {exercise.target_muscle && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground dark:text-gray-400">{t("targetMuscle")}</h3>
                  <p className="text-base dark:text-white">{exercise.target_muscle}</p>
                </div>
              )}
            </div>

            {exercise.instructions && exercise.instructions.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground dark:text-gray-400">{t("custom.instruction")}</h3>
                <ul className="list-decimal pl-4 space-y-2">
                  {exercise.instructions.map((instruction, index) => (
                    <li key={index} className="text-sm dark:text-white">{instruction}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
